<template>
  <!--
    The ONE renderer for a subscription plan's description on public surfaces.
    Every place that shows the description mounts this component, so the
    sanitizer, the legacy-plain-text handling and the empty fallback cannot
    drift between surfaces.

    SECURITY (stored-XSS, CWE-79): the description is Super-Admin-authored
    rich text. The API sanitizes it on write; this component sanitizes AGAIN
    (DOMPurify, sanitizePlanDescription) before v-html because the column is
    craftable directly. The fallback is app copy, rendered as TEXT, never HTML.
  -->
  <!-- eslint-disable vue/no-v-html -->
  <div
    v-if="html"
    class="plan-description"
    :class="{ 'plan-description--plain': !rich }"
    :data-testid="testId"
    v-html="html"
  ></div>
  <div
    v-else
    class="plan-description plan-description--plain"
    :data-testid="testId"
  >{{ fallback }}</div>
</template>

<script>
import {
  sanitizePlanDescription,
  planDescriptionText,
  isRichDescription,
} from "@/utils/sanitizePlanDescription";

export default {
  name: "PlanDescription",

  props: {
    // The plan's stored description: sanitized HTML, legacy plain text, or
    // null/empty.
    description: { type: String, default: null },
    // Shown (as plain text) when there is no visible description.
    fallback: { type: String, default: "" },
    // Per-plan test id (several cards share one page).
    testId: { type: String, default: "plan-description" },
  },

  computed: {
    // "" when there is nothing visible (null, blank, or an emptied editor's
    // `<p></p>`), so the fallback shows rather than an empty box.
    html() {
      if (!planDescriptionText(this.description)) return "";
      return sanitizePlanDescription(this.description);
    },
    rich() {
      return isRichDescription(this.description);
    },
  },
};
</script>

<style scoped>
/* Plain (legacy / fallback) text keeps the line breaks the author typed. */
.plan-description--plain {
  white-space: pre-line;
}

.plan-description :deep(p) {
  margin: 0 0 0.6em;
}

.plan-description :deep(p:last-child),
.plan-description :deep(ul:last-child),
.plan-description :deep(ol:last-child) {
  margin-bottom: 0;
}

/* The card centers its copy; a list reads left-aligned and inset. */
.plan-description :deep(ul),
.plan-description :deep(ol) {
  text-align: left;
  margin: 0 0 0.6em;
  padding-left: 1.4em;
}

.plan-description :deep(a) {
  color: #0b6fb8;
  text-decoration: underline;
}
</style>
